import { useState, useMemo } from 'react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Search, SlidersHorizontal, MapPin, Star, Clock, DollarSign, Bookmark, ChevronDown, Users, TrendingUp, Sparkles, ShoppingBag, Utensils, Truck, Store, MessageSquare, Map as MapIcon, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import FilterModal from '@/components/FilterModal';
import LocationSelectionModal from '@/components/LocationSelectionModal';
import DistanceDisplay from '@/components/DistanceDisplay';
import DistanceSelectorSheet from '@/components/DistanceSelectorSheet';
import TopHeader from '@/components/TopHeader';
const kudozIcon = "/kudoz-logo-circular.png";
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useAuth } from '@/hooks/useAuth';
import { useSessionLocation } from '@/hooks/useSessionLocation';
import { proxyIfCdn } from '@/hooks/useAuthenticatedMedia';
import { ReorderValidationModal } from '@/components/ReorderValidationModal';
import AuthPromptBanner from '@/components/AuthPromptBanner';

// Restaurant type matching the new API response
interface LocalRestaurant {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  category: string | null;
  priceRange: string | null;
  overallRating: number;
  totalReviews: number;
  recentReviews: number;
  latitude: string | null;
  longitude: string | null;
  distanceKm: number | null;
  orderingEnabled: boolean;
  eatzyClientId: string | null;
  hasDelivery: boolean;
  hasTakeout: boolean;
  hasReservations: boolean;
  logoImageUrl: string | null;
  primaryImageUrl: string | null;
  eatzyBannerUrl: string | null;
  eatzyDeliveryFee: string | null;
  eatzyMinimumOrder: string | null;
  eatzyDelays: { takeout?: number; delivery?: number } | null;
  friendReviewCount: number;
  tags: string[];
  reviewTags: string[];
  // Live delivery data from Eatzy API
  isOpen?: boolean;
  routeDuration?: number;
  deliveryWindow?: number;
  // Hours data for fallback open/closed calculation
  hoursJson?: Record<string, unknown>;
  eatzyTimezone?: string;
  kudozExternalId?: number | null;
}

// Delivery info from Eatzy API
interface DeliveryInfo {
  eatzyId: number;
  profile: string;
  fees: Record<string, number>;
  delays: { takeout?: number; delivery?: number };
  windows: { takeout?: number; delivery?: number };
  routeDuration?: number;
  open: boolean;
  modes: { takeout: boolean; delivery: boolean; reservations: boolean };
}

interface DeliveryInfoResponse {
  success: boolean;
  deliveryInfo: Record<string, DeliveryInfo>;
  count: number;
  error?: string;
}

interface OrderItem {
  itemId: string;
  itemName: string;
  quantity: number;
  price: number;
}

interface UserOrder {
  id: string;
  restaurantId: string;
  type: string;
  status: string;
  items: OrderItem[];
  total: number | null;
  createdAt: string;
  reviewCreated: boolean;
  restaurant: { id: string; name: string; city: string | null; state: string | null; address: string | null } | null;
}

interface BookmarkWithRestaurant {
  id: string;
  restaurantId: string;
  userId: string;
  createdAt: string;
  restaurant: LocalRestaurant | null;
}

// Food-themed placeholder images using picsum with grayscale for consistent look
const FOOD_PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=300&fit=crop',
  'https://images.unsplash.com/photo-1493770348161-369560ae357d?w=400&h=300&fit=crop',
  'https://images.unsplash.com/photo-1476224203421-9ac39bcb3327?w=400&h=300&fit=crop',
  'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=400&h=300&fit=crop',
  'https://images.unsplash.com/photo-1565958011703-44f9829ba187?w=400&h=300&fit=crop',
  'https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=400&h=300&fit=crop',
  'https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=400&h=300&fit=crop',
  'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=400&h=300&fit=crop',
];

// Get a consistent placeholder image based on restaurant name
function getPlaceholderImage(category: string | null, name: string): string {
  // Generate a consistent index based on the restaurant name
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash << 5) - hash) + name.charCodeAt(i);
    hash = hash & hash;
  }
  const index = Math.abs(hash) % FOOD_PLACEHOLDER_IMAGES.length;
  return FOOD_PLACEHOLDER_IMAGES[index];
}

// Calculate if restaurant is currently open based on hoursJson
function calculateIsOpenFromHours(
  hoursJson: Record<string, unknown> | undefined,
  timezone: string | undefined,
  serviceType: 'delivery' | 'takeout' = 'delivery'
): boolean | undefined {
  if (!hoursJson || typeof hoursJson !== 'object') return undefined;
  
  try {
    const hours = hoursJson as Record<string, { 
      open?: Record<string, string>;
      close?: Record<string, string>;
      open2?: Record<string, string>;
      close2?: Record<string, string>;
    }>;
    
    const serviceHours = hours[serviceType] || hours.business;
    if (!serviceHours || typeof serviceHours !== 'object') return undefined;
    
    // Get current time in restaurant's timezone
    const tz = timezone || 'America/Denver';
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = { 
      timeZone: tz, 
      weekday: 'long',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    };
    const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(now);
    const weekday = parts.find(p => p.type === 'weekday')?.value?.toLowerCase() || '';
    const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
    const minute = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
    const currentMinutes = hour * 60 + minute;
    
    // Get previous day for overnight window checks
    const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDayIndex = WEEKDAYS.indexOf(weekday);
    const previousDay = WEEKDAYS[(currentDayIndex + 6) % 7];
    
    // Helper to check if time is within a window (handles overnight)
    const isInWindow = (
      openStr: string | undefined, 
      closeStr: string | undefined,
      checkPreviousDay = false
    ): boolean => {
      if (!openStr || !closeStr) return false;
      const [openH, openM] = openStr.split(':').map(Number);
      const [closeH, closeM] = closeStr.split(':').map(Number);
      if (isNaN(openH) || isNaN(openM) || isNaN(closeH) || isNaN(closeM)) return false;
      
      const openMinutes = openH * 60 + openM;
      const closeMinutes = closeH * 60 + closeM;
      
      // Normal case: opens and closes same day
      if (closeMinutes > openMinutes) {
        return currentMinutes >= openMinutes && currentMinutes < closeMinutes;
      }
      
      // Overnight case: closes after midnight (e.g., open 18:00, close 02:00)
      if (closeMinutes < openMinutes) {
        if (checkPreviousDay) {
          // We're checking previous day's overnight window
          // If current time is before close (early morning), we're in the overnight window
          return currentMinutes < closeMinutes;
        } else {
          // We're checking today's window
          // If current time is after open, we're in the overnight window (evening)
          return currentMinutes >= openMinutes;
        }
      }
      
      return false;
    };
    
    // Check today's first window
    const openTime = serviceHours.open?.[weekday];
    const closeTime = serviceHours.close?.[weekday];
    if (isInWindow(openTime, closeTime, false)) return true;
    
    // Check today's second window
    const open2Time = serviceHours.open2?.[weekday];
    const close2Time = serviceHours.close2?.[weekday];
    if (isInWindow(open2Time, close2Time, false)) return true;
    
    // Check previous day's overnight windows (if we're after midnight)
    if (currentMinutes < 360) { // Before 6 AM, check previous day's overnight
      const prevOpenTime = serviceHours.open?.[previousDay];
      const prevCloseTime = serviceHours.close?.[previousDay];
      if (isInWindow(prevOpenTime, prevCloseTime, true)) return true;
      
      const prevOpen2Time = serviceHours.open2?.[previousDay];
      const prevClose2Time = serviceHours.close2?.[previousDay];
      if (isInWindow(prevOpen2Time, prevClose2Time, true)) return true;
    }
    
    // Has hours but not currently in any open window
    return false;
  } catch (e) {
    console.warn('Error calculating open status from hours:', e);
    return undefined;
  }
}

interface RestaurantListProps {
  serviceType?: 'delivery' | 'takeout';
  location?: string;
  latitude?: number;
  longitude?: number;
  timing?: 'asap' | 'scheduled';
  scheduledTime?: string;
  radius?: number;
  onEditParams?: () => void;
  embedded?: boolean;
}

export default function RestaurantList({
  serviceType = 'delivery',
  location = '',
  latitude: propLatitude,
  longitude: propLongitude,
  timing = 'asap',
  scheduledTime = '',
  radius = 10,
  onEditParams,
  embedded = false
}: RestaurantListProps) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { primaryAddress, profile: userProfile } = useUserProfile();
  
  const { data: unreadMessageData } = useQuery<{ count: number }>({
    queryKey: ['/api/user/unread-messages-count'],
    enabled: !!user && !embedded,
    refetchInterval: 60000,
  });
  
  const headerUser = !embedded && user ? {
    id: String(user.id),
    name: user.name || user.email?.split('@')[0] || 'User',
    email: user.email,
    avatar: user.avatar,
    kudozBalance: (user as any).kudozBalance || 0,
  } : undefined;
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'relevance' | 'rating' | 'distance' | 'reviews' | 'name'>('relevance');
  const [priceFilter, setPriceFilter] = useState<string[]>([]);
  const [cuisineFilter, setCuisineFilter] = useState<string[]>([]);
  const [serviceFilter, setServiceFilter] = useState<string[]>([]);
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [tagFilters, setTagFilters] = useState<{ include: string[]; exclude: string[] }>({ include: [], exclude: [] });
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const { selectedLocationName, customLatitude, customLongitude, setSessionLocation } = useSessionLocation();
  const [showDistanceSheet, setShowDistanceSheet] = useState(false);
  const [selectedDistance, setSelectedDistance] = useState<number | null>(10); // Default 10 miles
  const [reorderModalData, setReorderModalData] = useState<{
    orderId: string;
    restaurantId: string;
    eatzyClientId: string;
    serviceType: 'takeout' | 'delivery';
  } | null>(null);

  // Determine location coordinates - use custom selection, then props, then primary address
  const effectiveLatitude = customLatitude ?? propLatitude ?? primaryAddress?.latitude;
  const effectiveLongitude = customLongitude ?? propLongitude ?? primaryAddress?.longitude;
  const effectiveLocation = selectedLocationName || location || primaryAddress?.streetAddress || 'Select a location';
  const hasValidLocation = effectiveLatitude !== undefined && effectiveLongitude !== undefined;

  // Handle location selection from modal
  const handleLocationSelect = (locationData: { name: string; address: string; coordinates?: { lat: number; lng: number } } | 'current') => {
    if (locationData === 'current') {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setSessionLocation('Current Location', position.coords.latitude, position.coords.longitude);
            setShowLocationModal(false);
          },
          () => {
            toast({
              title: 'Location Error',
              description: 'Could not get your current location. Please try a saved address.',
              variant: 'destructive'
            });
          }
        );
      } else {
        toast({
          title: 'Location Error',
          description: 'Geolocation is not supported by your browser',
          variant: 'destructive'
        });
      }
    } else {
      if (locationData.coordinates) {
        setSessionLocation(locationData.name, locationData.coordinates.lat, locationData.coordinates.lng);
        setShowLocationModal(false);
      } else {
        toast({
          title: 'Address Selected',
          description: 'This address doesn\'t have coordinates for distance calculations. Results will not be sorted by distance.',
        });
        setSessionLocation(locationData.name);
        setShowLocationModal(false);
      }
    }
  };

  // Build query params for the API
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (effectiveLatitude) params.set('latitude', String(effectiveLatitude));
    if (effectiveLongitude) params.set('longitude', String(effectiveLongitude));
    if (priceFilter.length > 0) params.set('priceRange', priceFilter.join(','));
    if (cuisineFilter.length > 0) params.set('cuisines', cuisineFilter.join(','));
    if (serviceFilter.length > 0) params.set('services', serviceFilter.join(','));
    // Convert miles to km for API (1 mile = 1.60934 km)
    const radiusKm = selectedDistance !== null ? selectedDistance * 1.60934 : null;
    if (radiusKm !== null) params.set('maxDistance', String(Math.round(radiusKm)));
    params.set('sortBy', sortBy);
    if (user?.id) params.set('userId', String(user.id));
    params.set('limit', '50');
    return params.toString();
  }, [searchQuery, effectiveLatitude, effectiveLongitude, priceFilter, cuisineFilter, serviceFilter, selectedDistance, sortBy, user?.id]);

  // Fetch restaurants from local database
  const { data, isLoading, isError, refetch } = useQuery<{
    success: boolean;
    restaurants: LocalRestaurant[];
    total: number;
    hasMore: boolean;
  }>({
    queryKey: ['/api/restaurants/list', queryParams],
    queryFn: async () => {
      const response = await fetch(`/api/restaurants/list?${queryParams}`);
      if (!response.ok) {
        throw new Error('Failed to fetch restaurants');
      }
      return response.json();
    },
  });

  // Fetch delivery info from Eatzy API (only when we have valid location)
  const deliveryQueryParams = useMemo(() => {
    if (!hasValidLocation) return null;
    const params = new URLSearchParams();
    params.set('lat', String(effectiveLatitude));
    params.set('lng', String(effectiveLongitude));
    params.set('mode', serviceType);
    // Map 'scheduled' to 'target' for Eatzy API compatibility
    params.set('type', timing === 'scheduled' ? 'target' : 'asap');
    return params.toString();
  }, [effectiveLatitude, effectiveLongitude, serviceType, timing, hasValidLocation]);

  const { data: deliveryData } = useQuery<DeliveryInfoResponse>({
    queryKey: ['/api/restaurants/delivery-info', deliveryQueryParams],
    queryFn: async () => {
      if (!deliveryQueryParams) return { success: false, deliveryInfo: {}, count: 0 };
      const response = await fetch(`/api/restaurants/delivery-info?${deliveryQueryParams}`);
      if (!response.ok) {
        return { success: false, deliveryInfo: {}, count: 0 };
      }
      return response.json();
    },
    enabled: hasValidLocation && !!deliveryQueryParams,
    staleTime: 60000, // Cache for 1 minute
  });

  const deliveryInfoMap = deliveryData?.deliveryInfo || {};

  // Merge local restaurant data with delivery info
  const restaurants = useMemo(() => {
    const baseRestaurants = data?.restaurants || [];
    
    // Enrich restaurants with delivery info if available
    return baseRestaurants.map(restaurant => {
      const eatzyProfile = restaurant.eatzyClientId;
      const deliveryInfo = eatzyProfile ? deliveryInfoMap[eatzyProfile] : null;
      
      if (deliveryInfo) {
        return {
          ...restaurant,
          // Override with live delivery data when available
          hasDelivery: deliveryInfo.modes.delivery,
          hasTakeout: deliveryInfo.modes.takeout,
          hasReservations: deliveryInfo.modes.reservations,
          eatzyDelays: deliveryInfo.delays,
          eatzyDeliveryFee: deliveryInfo.fees['Delivery Fee']?.toString() || restaurant.eatzyDeliveryFee,
          // Add new fields for UI
          isOpen: deliveryInfo.open,
          routeDuration: deliveryInfo.routeDuration,
          deliveryWindow: deliveryInfo.windows?.delivery,
        };
      }
      
      // Fallback: If restaurant has hours data but not in delivery API response,
      // calculate isOpen from hoursJson (for Eatzy restaurants not in bulk API)
      if (restaurant.orderingEnabled && restaurant.hoursJson) {
        const calculatedIsOpen = calculateIsOpenFromHours(
          restaurant.hoursJson,
          restaurant.eatzyTimezone,
          serviceType
        );
        return {
          ...restaurant,
          isOpen: calculatedIsOpen,
        };
      }
      
      return restaurant;
    });
  }, [data?.restaurants, deliveryInfoMap, serviceType]);

  // Apply client-side filtering for tags and bookmarks (bookmarks need to be fetched separately)
  const filteredRestaurants = useMemo(() => {
    let result = [...restaurants];

    // Filter by tags
    if (tagFilters.include.length > 0) {
      result = result.filter(r => 
        tagFilters.include.some(tag => r.tags.includes(tag.toLowerCase()))
      );
    }
    if (tagFilters.exclude.length > 0) {
      result = result.filter(r => 
        !tagFilters.exclude.some(tag => r.tags.includes(tag.toLowerCase()))
      );
    }

    // Stable secondary sort: open first, unknown second, closed last
    // Preserves the existing server-side sort order within each group
    const openRank = (r: LocalRestaurant) =>
      r.isOpen === true ? 0 : r.isOpen === false ? 2 : 1;
    result.sort((a, b) => openRank(a) - openRank(b));

    return result;
  }, [restaurants, tagFilters]);

  const topRatedRestaurants = useMemo(() => {
    return [...filteredRestaurants]
      .filter(r => r.totalReviews >= 2)
      .sort((a, b) => parseFloat(String(b.overallRating)) - parseFloat(String(a.overallRating)))
      .slice(0, 5);
  }, [filteredRestaurants]);


  const formatTiming = () => {
    if (timing === 'asap') return 'ASAP';
    if (scheduledTime) {
      const date = new Date(scheduledTime);
      return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        hour: 'numeric', 
        minute: '2-digit' 
      });
    }
    return 'Scheduled';
  };

  // Truncate location for summary display
  const truncatedLocation = effectiveLocation.length > 30 
    ? effectiveLocation.substring(0, 30) + '...' 
    : effectiveLocation;

  // Format distance for display
  const formatDistance = (distanceKm: number | null) => {
    if (distanceKm === null) return null;
    const miles = distanceKm * 0.621371;
    return miles < 0.1 ? '< 0.1 mi' : `${miles.toFixed(1)} mi`;
  };

  // Format delivery time from delays
  const formatDeliveryTime = (delays: { takeout?: number; delivery?: number } | null, service: 'delivery' | 'takeout') => {
    if (!delays) return null;
    const seconds = service === 'delivery' ? delays.delivery : delays.takeout;
    if (!seconds) return null;
    const minutes = Math.round(seconds / 60);
    return `${minutes}-${minutes + 15} min`;
  };

  return (
    <div className={cn(
      "flex flex-col",
      embedded ? "min-h-full" : "h-full"
    )}>
      {/* TopHeader - Only shown when not embedded */}
      {!embedded && (
        <TopHeader
          user={headerUser}
          unreadAlertCount={(userProfile as any)?.unreadAlertCount || 0}
          unreadMessageCount={unreadMessageData?.count || 0}
          onNotificationsClick={() => navigate('/profile?section=alerts')}
          onMessagesClick={() => navigate('/profile?section=comments')}
          onProfileClick={() => navigate('/profile')}
        />
      )}

      {!embedded && <AuthPromptBanner />}
      
      {/* Controls Header - Compact with Collapsible Details */}
      <div className="bg-card border-b border-border px-4 py-3 space-y-2 flex-shrink-0">
        {/* Row 1: Clickable Location and Distance Selectors */}
        <div 
          className="flex items-center gap-2"
          data-testid="header-order-summary"
        >
          <button
            onClick={() => setShowLocationModal(true)}
            className="flex items-center gap-1.5 min-w-0 text-sm hover:bg-muted/50 rounded-md px-2 py-1 transition-colors"
            data-testid="button-location-select"
          >
            <MapPin className="w-3.5 h-3.5 text-primary flex-shrink-0" />
            <span className="truncate font-medium max-w-[150px]" data-testid="text-location">{truncatedLocation}</span>
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          </button>
          {hasValidLocation && (
            <DistanceDisplay
              distance={selectedDistance}
              onClick={() => setShowDistanceSheet(true)}
              data-testid="button-distance-select"
            />
          )}
          <div className="flex-1" />
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            onClick={() => navigate('/restaurants/map')}
            data-testid="button-map-view"
          >
            <MapIcon className="w-4 h-4" />
            Map
          </Button>
          <div className="flex items-center gap-1 flex-shrink-0">
            {onEditParams && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsHeaderExpanded(!isHeaderExpanded)}
                data-testid="button-expand-header"
              >
                <ChevronDown className={cn(
                  "w-4 h-4 transition-transform duration-200",
                  isHeaderExpanded && "rotate-180"
                )} />
              </Button>
            )}
          </div>
        </div>

        {/* Expanded Details - Collapsible */}
        {isHeaderExpanded && (
          <div className="space-y-2 pt-1 border-t border-border/50">
            {/* Full Location Display */}
            <div className="flex items-start gap-2 px-2 py-1.5 bg-muted/30 rounded-md">
              <MapPin className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Your Location</div>
                <div className="text-sm font-medium break-words">{effectiveLocation}</div>
              </div>
            </div>

            {/* Service Filter */}
            <div className="flex items-center gap-2 px-2">
              <span className="text-xs text-muted-foreground">Services:</span>
              <div className="flex gap-1">
                <Badge 
                  variant={serviceFilter.includes('delivery') ? 'default' : 'outline'}
                  className="cursor-pointer text-xs"
                  onClick={() => {
                    setServiceFilter(prev => 
                      prev.includes('delivery') 
                        ? prev.filter(s => s !== 'delivery')
                        : [...prev, 'delivery']
                    );
                  }}
                >
                  <Truck className="w-3 h-3 mr-1" />
                  Delivery
                </Badge>
                <Badge 
                  variant={serviceFilter.includes('takeout') ? 'default' : 'outline'}
                  className="cursor-pointer text-xs"
                  onClick={() => {
                    setServiceFilter(prev => 
                      prev.includes('takeout') 
                        ? prev.filter(s => s !== 'takeout')
                        : [...prev, 'takeout']
                    );
                  }}
                >
                  <Store className="w-3 h-3 mr-1" />
                  Takeout
                </Badge>
                <Badge 
                  variant={serviceFilter.includes('ordering') ? 'default' : 'outline'}
                  className="cursor-pointer text-xs"
                  onClick={() => {
                    setServiceFilter(prev => 
                      prev.includes('ordering') 
                        ? prev.filter(s => s !== 'ordering')
                        : [...prev, 'ordering']
                    );
                  }}
                >
                  <ShoppingBag className="w-3 h-3 mr-1" />
                  Order Online
                </Badge>
              </div>
            </div>

            {/* Price Filter */}
            <div className="flex items-center gap-2 px-2">
              <span className="text-xs text-muted-foreground">Price:</span>
              <div className="flex gap-1">
                {['$', '$$', '$$$', '$$$$'].map(price => (
                  <Badge 
                    key={price}
                    variant={priceFilter.includes(price) ? 'default' : 'outline'}
                    className="cursor-pointer text-xs"
                    onClick={() => {
                      setPriceFilter(prev => 
                        prev.includes(price) 
                          ? prev.filter(p => p !== price)
                          : [...prev, price]
                      );
                    }}
                  >
                    {price}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Row 2: Search + Controls - Always Visible */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[140px]">
            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search restaurants, cuisines..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
              data-testid="input-search-restaurants"
            />
          </div>
          <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
            <SelectTrigger className="w-28 h-9" data-testid="select-sort">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="relevance">Relevance</SelectItem>
              <SelectItem value="rating">Rating</SelectItem>
              <SelectItem value="distance">Distance</SelectItem>
              <SelectItem value="reviews">Reviews</SelectItem>
              <SelectItem value="name">Name</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => setShowFilterModal(true)}
            data-testid="button-filter"
          >
            <SlidersHorizontal className="w-4 h-4" />
          </Button>
        </div>

        {/* Active Filters Display */}
        {(priceFilter.length > 0 || serviceFilter.length > 0 || cuisineFilter.length > 0) && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">Filters:</span>
            {priceFilter.map(p => (
              <Badge 
                key={p} 
                variant="secondary" 
                className="text-xs cursor-pointer"
                onClick={() => setPriceFilter(prev => prev.filter(x => x !== p))}
              >
                {p} ×
              </Badge>
            ))}
            {serviceFilter.map(s => (
              <Badge 
                key={s} 
                variant="secondary" 
                className="text-xs cursor-pointer"
                onClick={() => setServiceFilter(prev => prev.filter(x => x !== s))}
              >
                {s} ×
              </Badge>
            ))}
            {(priceFilter.length > 0 || serviceFilter.length > 0) && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 text-xs"
                onClick={() => {
                  setPriceFilter([]);
                  setServiceFilter([]);
                  setCuisineFilter([]);
                }}
              >
                Clear all
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Two-column layout: restaurant list + right panel (desktop) */}
      <div className="flex-1 overflow-hidden lg:grid lg:grid-cols-[1fr_320px]">

        {/* Left: Restaurant List */}
        <div className="overflow-y-auto px-4 pt-4 pb-24 space-y-3 h-full">

        {/* Mobile-only: Order Again horizontal strip */}
        <div className="lg:hidden">
          <OrderAgainStrip onReorder={(order) => {
            setReorderModalData({
              orderId: String(order.id),
              restaurantId: order.restaurant.profile || String(order.restaurant.id),
              eatzyClientId: order.restaurant.profile || String(order.restaurant.id),
              serviceType: (order.type === 'delivery' ? 'delivery' : 'takeout'),
            });
          }} />
        </div>

        {/* Results Count */}
        {!isLoading && (
          <div className="text-sm text-muted-foreground mb-2">
            {filteredRestaurants.length} restaurant{filteredRestaurants.length !== 1 ? 's' : ''} found
            {hasValidLocation && ' near you'}
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Card key={i} className="p-3">
                <div className="flex gap-3">
                  <Skeleton className="w-24 h-24 rounded-md" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-4 w-2/3" />
                    <div className="flex gap-2">
                      <Skeleton className="h-6 w-16" />
                      <Skeleton className="h-6 w-16" />
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Error State */}
        {isError && (
          <div className="text-center py-12">
            <p className="text-destructive mb-2">Failed to load restaurants</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Try Again
            </Button>
          </div>
        )}

        {/* No Location Hint */}
        {!hasValidLocation && !isLoading && (
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-4">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Add an address with coordinates to see restaurants sorted by distance.
            </p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredRestaurants.length === 0 ? (
          <div className="text-center py-12">
            <Utensils className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No restaurants found matching your criteria</p>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-3"
              onClick={() => {
                setSearchQuery('');
                setPriceFilter([]);
                setServiceFilter([]);
                setCuisineFilter([]);
                setTagFilters({ include: [], exclude: [] });
              }}
            >
              Clear Filters
            </Button>
          </div>
        ) : !isLoading && (
          filteredRestaurants.map((restaurant) => (
            <Card 
              key={restaurant.id} 
              className="hover-elevate active-elevate-2 cursor-pointer overflow-hidden relative"
              onClick={() => {
                navigate(`/restaurant/${restaurant.id}`);
              }}
              data-testid={`restaurant-card-${restaurant.id}`}
            >
              <CardContent className="p-0">
                <div className="flex gap-3 p-3">
                  {/* Restaurant Image */}
                  <div className="w-24 h-24 rounded-md overflow-hidden flex-shrink-0 bg-muted">
                    <img
                      src={proxyIfCdn(restaurant.primaryImageUrl || restaurant.logoImageUrl || restaurant.eatzyBannerUrl) || getPlaceholderImage(restaurant.category, restaurant.name)}
                      alt={restaurant.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        // Fallback to Utensils icon if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.parentElement?.classList.add('flex', 'items-center', 'justify-center');
                        const icon = document.createElement('div');
                        icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-muted-foreground"><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/></svg>';
                        target.parentElement?.appendChild(icon);
                      }}
                    />
                  </div>

                  {/* Restaurant Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-base truncate">{restaurant.name}</h3>
                      {restaurant.orderingEnabled && (
                        <Button 
                          size="sm" 
                          className="h-7 text-xs flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/restaurant/${restaurant.id}`);
                          }}
                        >
                          <ShoppingBag className="w-3 h-3 mr-1" />
                          Order
                        </Button>
                      )}
                    </div>
                    
                    {/* Rating and basic info */}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {Number(restaurant.overallRating) > 0 ? (
                        <div className="flex items-center gap-1">
                          <Star className="w-3.5 h-3.5 fill-yellow-500 text-yellow-500" />
                          <span className="text-sm font-semibold">{Number(restaurant.overallRating).toFixed(1)}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">No ratings yet</span>
                      )}
                      
                      {/* Review count - always visible */}
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <MessageSquare className="w-3.5 h-3.5" />
                        <span className="text-sm">
                          {restaurant.totalReviews > 0 
                            ? `${restaurant.totalReviews} review${restaurant.totalReviews !== 1 ? 's' : ''}`
                            : 'No reviews'
                          }
                        </span>
                      </div>
                      
                      {restaurant.priceRange && (
                        <>
                          <span className="text-muted-foreground">•</span>
                          <span className="text-sm text-muted-foreground">{restaurant.priceRange}</span>
                        </>
                      )}
                    </div>

                    {/* Row 2: Category + Promotional badges (New, Trending, Popular, Top Rated) */}
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {restaurant.category && (
                        <span className="text-sm text-muted-foreground">
                          {restaurant.category}
                        </span>
                      )}
                      {restaurant.tags.includes('new') && (
                        <Badge variant="secondary" className="text-xs px-1.5 py-0 bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20">
                          <Sparkles className="w-3 h-3 mr-0.5" />
                          New
                        </Badge>
                      )}
                      {restaurant.tags.includes('trending') && (
                        <Badge variant="secondary" className="text-xs px-1.5 py-0 bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20">
                          <TrendingUp className="w-3 h-3 mr-0.5" />
                          Trending
                        </Badge>
                      )}
                      {restaurant.tags.includes('popular') && (
                        <Badge variant="secondary" className="text-xs px-1.5 py-0 bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20">
                          <Star className="w-3 h-3 mr-0.5" />
                          Popular
                        </Badge>
                      )}
                      {restaurant.tags.includes('top-rated') && (
                        <Badge variant="secondary" className="text-xs px-1.5 py-0 bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20">
                          <Star className="w-3 h-3 mr-0.5 fill-current" />
                          Top Rated
                        </Badge>
                      )}
                    </div>

                    {/* Row 3: Address - standardized format: Street, City · distance */}
                    <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">
                        {(() => {
                          let addressStr = 'Location available';
                          if (restaurant.address) {
                            // Check if address already contains city to avoid duplication
                            const addressContainsCity = restaurant.city && 
                              restaurant.address.toLowerCase().includes(restaurant.city.toLowerCase());
                            addressStr = addressContainsCity ? restaurant.address : 
                              (restaurant.city ? `${restaurant.address}, ${restaurant.city}` : restaurant.address);
                          } else if (restaurant.city) {
                            addressStr = restaurant.city;
                          }
                          const distanceStr = restaurant.distanceKm !== null ? formatDistance(restaurant.distanceKm) : null;
                          return distanceStr ? `${addressStr} · ${distanceStr}` : addressStr;
                        })()}
                      </span>
                    </div>

                    {/* Row 4: Operational info (Open/Closed, Delivery, Takeout) */}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {restaurant.isOpen !== undefined && (
                        <Badge 
                          variant="secondary" 
                          className={cn(
                            "text-xs px-2 py-0",
                            restaurant.isOpen 
                              ? "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20"
                              : "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20"
                          )}
                        >
                          <Clock className="w-3 h-3 mr-1" />
                          {restaurant.isOpen ? 'Open' : 'Closed'}
                        </Badge>
                      )}
                      {restaurant.hasDelivery && (
                        <Badge variant="outline" className="text-xs px-2 py-0">
                          <Truck className="w-3 h-3 mr-1" />
                          Delivery
                          {restaurant.eatzyDelays && formatDeliveryTime(restaurant.eatzyDelays, 'delivery') && (
                            <span className="ml-1 text-muted-foreground">
                              {formatDeliveryTime(restaurant.eatzyDelays, 'delivery')}
                            </span>
                          )}
                          {restaurant.eatzyDeliveryFee && (
                            <span className="ml-1 text-muted-foreground">
                              · ${restaurant.eatzyDeliveryFee}
                            </span>
                          )}
                        </Badge>
                      )}
                      {restaurant.hasTakeout && (
                        <Badge variant="outline" className="text-xs px-2 py-0">
                          <Store className="w-3 h-3 mr-1" />
                          Takeout
                        </Badge>
                      )}
                      {restaurant.routeDuration && (
                        <span className="text-xs text-muted-foreground">
                          ~{Math.round(restaurant.routeDuration / 60)} min away
                        </span>
                      )}
                    </div>

                    {/* Row 5: Social proof (Friends reviewed + Review tags) */}
                    {(() => {
                      const categoryLower = restaurant.category?.toLowerCase() || '';
                      const categoryWords = categoryLower.split(/[\s,]+/).filter(w => w.length > 2);
                      const filteredTags = (restaurant.reviewTags || []).filter(tag => {
                        const tagLower = tag.toLowerCase();
                        if (tagLower === categoryLower) return false;
                        if (categoryWords.some(word => tagLower === word || tagLower.includes(word))) return false;
                        return true;
                      });
                      
                      const hasFriendReviews = restaurant.friendReviewCount > 0;
                      if (filteredTags.length === 0 && !hasFriendReviews) return null;
                      
                      return (
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                          {hasFriendReviews && (
                            <Badge variant="secondary" className="text-xs px-1.5 py-0 bg-primary/10 text-primary border-primary/20">
                              <Users className="w-3 h-3 mr-0.5" />
                              {restaurant.friendReviewCount} friend{restaurant.friendReviewCount !== 1 ? 's' : ''}
                            </Badge>
                          )}
                          {filteredTags.slice(0, 4).map((tag, idx) => (
                            <Badge 
                              key={idx} 
                              variant="outline" 
                              className="text-xs px-1.5 py-0 bg-muted/50 text-muted-foreground border-muted-foreground/20"
                            >
                              #{tag}
                            </Badge>
                          ))}
                          {filteredTags.length > 4 && (
                            <span className="text-xs text-muted-foreground">
                              +{filteredTags.length - 4} more
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
        </div>
        {/* Right panel — desktop only */}
        <aside className="hidden lg:flex flex-col border-l border-border overflow-y-auto">
          <RestaurantsRightPanel topRestaurants={topRatedRestaurants} />
        </aside>
      </div>

      <FilterModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onApplyFilters={(filters) => {
          setTagFilters(filters);
          setShowFilterModal(false);
        }}
        initialFilters={tagFilters}
        selectedExperienceTypes={serviceFilter.length > 0 ? serviceFilter as ('delivery' | 'takeout' | 'dine-in')[] : ['delivery', 'takeout']}
      />

      <LocationSelectionModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        onLocationSelect={handleLocationSelect}
        selectedLocation={effectiveLocation}
      />

      <DistanceSelectorSheet
        isOpen={showDistanceSheet}
        onClose={() => setShowDistanceSheet(false)}
        currentDistance={selectedDistance}
        onDistanceChange={(distance) => {
          setSelectedDistance(distance);
          setShowDistanceSheet(false);
        }}
      />

      {reorderModalData && (
        <ReorderValidationModal
          isOpen={!!reorderModalData}
          onClose={() => setReorderModalData(null)}
          orderId={reorderModalData.orderId}
          restaurantId={reorderModalData.restaurantId}
          eatzyClientId={reorderModalData.eatzyClientId}
          serviceType={reorderModalData.serviceType}
          onSuccess={(info) => {
            setReorderModalData(null);
            if (info.action === 'modify' && info.restaurantId) {
              const isUuid = info.restaurantId.includes('-');
              navigate(isUuid ? `/restaurant/${info.restaurantId}` : `/r/${info.restaurantId}`);
            } else {
              navigate('/checkout');
            }
          }}
        />
      )}

    </div>
  );
}

// ─── Shared Eatzy order history types ──────────────────────────────────────

type EatzyOrderItem = { id: number; name: string; quantity: number; price: number };
type EatzyOrderEntry = {
  id: string;
  type?: string;
  restaurant: { id: number; name: string; logo?: string; profile?: string };
  items: EatzyOrderItem[];
  total: number;
  date: number;
};
type EatzyOrderHistoryResponse = { total_results: number; results: EatzyOrderEntry[] };

// ─── Mobile: Order Again horizontal strip ──────────────────────────────────

function OrderAgainStrip({ onReorder }: { onReorder: (order: EatzyOrderEntry) => void }) {
  const { isAuthenticated } = useAuth();

  const { data: historyData, isLoading } = useQuery<EatzyOrderHistoryResponse>({
    queryKey: ['/api/eatzy/consumer/customer/order/history', 'recent'],
    queryFn: async () => {
      const { authService } = await import('@/services/AuthService');
      const response = await authService.eatzyApiCall('/consumer/customer/order/history?page=1&query=');
      if (!response.ok) throw new Error(`Failed to fetch order history: ${response.status}`);
      return response.json();
    },
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
    retry: 1,
  });

  const uniqueOrders = useMemo(() => {
    const orders = historyData?.results || [];
    const seen = new Set<number>();
    return orders
      .filter(o => !seen.has(o.restaurant.id) && seen.add(o.restaurant.id))
      .slice(0, 6);
  }, [historyData]);

  if (!isAuthenticated || (!isLoading && uniqueOrders.length === 0)) return null;

  return (
    <div className="mb-3">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
        <RotateCcw className="w-3 h-3" />
        Order Again
      </p>
      <div className="flex gap-2 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {isLoading
          ? [1, 2, 3].map(i => (
              <div key={i} className="flex-shrink-0 w-36">
                <Skeleton className="h-16 w-36 rounded-lg" />
              </div>
            ))
          : uniqueOrders.map(order => (
              <button
                key={order.id}
                className="flex-shrink-0 bg-card border border-border rounded-lg p-2.5 text-left hover-elevate w-36"
                onClick={() => onReorder(order)}
              >
                <p className="text-sm font-medium truncate leading-tight">{order.restaurant.name}</p>
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {order.items.slice(0, 1).map(i => i.name).join('')}
                </p>
                <div className="flex items-center gap-1 mt-2">
                  <RotateCcw className="w-3 h-3 text-primary flex-shrink-0" />
                  <span className="text-xs text-primary font-medium">Reorder</span>
                </div>
              </button>
            ))
        }
      </div>
    </div>
  );
}

// ─── Desktop: Right panel with widgets ────────────────────────────────────

function RestaurantsRightPanel({ topRestaurants }: { topRestaurants: LocalRestaurant[] }) {
  const [, navigate] = useLocation();
  const { isAuthenticated } = useAuth();
  const [reorderModalData, setReorderModalData] = useState<{
    orderId: string;
    restaurantId: string;
    eatzyClientId: string;
    serviceType: 'takeout' | 'delivery';
  } | null>(null);

  const { data: historyData, isLoading: ordersLoading } = useQuery<EatzyOrderHistoryResponse>({
    queryKey: ['/api/eatzy/consumer/customer/order/history', 'recent'],
    queryFn: async () => {
      const { authService } = await import('@/services/AuthService');
      const response = await authService.eatzyApiCall('/consumer/customer/order/history?page=1&query=');
      if (!response.ok) throw new Error(`Failed to fetch order history: ${response.status}`);
      return response.json();
    },
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
    retry: 1,
  });

  const { data: bookmarks = [], isLoading: bookmarksLoading } = useQuery<BookmarkWithRestaurant[]>({
    queryKey: ['/api/user/bookmarks'],
    enabled: isAuthenticated,
  });

  const recentOrders = useMemo(() => {
    const orders = historyData?.results || [];
    const seen = new Set<number>();
    return orders
      .filter(o => !seen.has(o.restaurant.id) && seen.add(o.restaurant.id))
      .slice(0, 4);
  }, [historyData]);

  const savedRestaurants = useMemo(() => {
    return bookmarks.filter(b => b.restaurant).slice(0, 5);
  }, [bookmarks]);

  return (
    <div className="p-4 flex flex-col gap-4">

      {/* Order Again widget */}
      {isAuthenticated && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <RotateCcw className="w-4 h-4 text-primary" />
              Order Again
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 pt-0">
            {ordersLoading ? (
              [1, 2, 3].map(i => <Skeleton key={i} className="h-14 w-full" />)
            ) : recentOrders.length === 0 ? (
              <p className="text-xs text-muted-foreground">No previous orders yet</p>
            ) : (
              recentOrders.map(order => (
                <div key={order.id} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{order.restaurant.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {order.items.slice(0, 2).map(i => i.name).join(', ')}
                      {order.items.length > 2 ? ` +${order.items.length - 2}` : ''}
                    </p>
                    {order.total != null && (
                      <p className="text-xs text-muted-foreground">${order.total.toFixed(2)}</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setReorderModalData({
                        orderId: String(order.id),
                        restaurantId: order.restaurant.profile || String(order.restaurant.id),
                        eatzyClientId: order.restaurant.profile || String(order.restaurant.id),
                        serviceType: (order.type === 'delivery' ? 'delivery' : 'takeout'),
                      });
                    }}
                  >
                    Reorder
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {/* Saved Restaurants widget */}
      {isAuthenticated && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Bookmark className="w-4 h-4 text-yellow-500" />
              Saved
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 pt-0">
            {bookmarksLoading ? (
              [1, 2, 3].map(i => <Skeleton key={i} className="h-8 w-full" />)
            ) : savedRestaurants.length === 0 ? (
              <p className="text-xs text-muted-foreground">No saved restaurants yet</p>
            ) : (
              savedRestaurants.map(b => (
                <button
                  key={b.id}
                  className="flex flex-col text-left hover-elevate rounded-md px-1 py-0.5 w-full"
                  onClick={() => navigate(`/restaurant/${b.restaurantId}`)}
                >
                  <span className="text-sm font-medium truncate">{b.restaurant!.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {b.restaurant!.category || b.restaurant!.city || ''}
                  </span>
                </button>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {/* Top Rated Near You widget */}
      {topRestaurants.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Star className="w-4 h-4 text-yellow-500" />
              Top Rated Near You
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 pt-0">
            {topRestaurants.map((r, i) => (
              <button
                key={r.id}
                className="flex items-center gap-2 text-left hover-elevate rounded-md px-1 py-0.5 w-full"
                onClick={() => navigate(`/restaurant/${r.id}`)}
              >
                <span className="text-xs font-bold text-muted-foreground w-4 flex-shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium truncate block">{r.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {parseFloat(String(r.overallRating)).toFixed(1)} ★
                    {r.category ? ` · ${r.category}` : ''}
                  </span>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {reorderModalData && (
        <ReorderValidationModal
          isOpen={!!reorderModalData}
          onClose={() => setReorderModalData(null)}
          orderId={reorderModalData.orderId}
          restaurantId={reorderModalData.restaurantId}
          eatzyClientId={reorderModalData.eatzyClientId}
          serviceType={reorderModalData.serviceType}
          onSuccess={(info) => {
            setReorderModalData(null);
            if (info.action === 'modify' && info.restaurantId) {
              const isUuid = info.restaurantId.includes('-');
              navigate(isUuid ? `/restaurant/${info.restaurantId}` : `/r/${info.restaurantId}`);
            } else {
              navigate('/checkout');
            }
          }}
        />
      )}

    </div>
  );
}
